// Tài khoản thêm gần đây — cuối Tổng quan Quản lý (Đại lý, CTV; 31/10/2026): 5 cấp dưới mới nhất trong nhánh (GET
// /network/units — chung bộ nhớ với trang Hệ thống), bỏ đơn vị đã xoá. Mỗi dòng mở đơn vị đó trên sơ đồ (/network?unit=).
// Dòng phụ: loại · SĐT chủ, cấp dưới gián tiếp thì "dưới <cấp trên>"; bên phải việc cần xử lý đầu tiên (chưa có bảng
// giá…) và lúc thêm. Màn rộng (≥ 1280px) tách thêm cột SĐT chủ.

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { get } from '@/lib/api'
import { cn } from '@/lib/utils'
import { phoneDisplay } from '@/lib/format'
import { useViewportMode } from '@/hooks/useViewportMode'
import { Skeleton } from '@/components/ui/skeleton'
import { IndexBadge } from '@/components/ui/data-table'
import { LoadError } from '@/components/ui/data-notice'
import { networkTypeMeta } from '@/components/network/NetworkTreeRow'
import { networkDownlineIssues } from '@/components/network/network-view'
import type { NetworkUnit } from '@/routes/network/DownlinePanel'
import { recentWhen } from './RecentOrders'

const LIMIT = 5

const SEVERITY = { danger: 'bg-danger/10 text-danger', warn: 'bg-warn/10 text-warn' } as const

export function RecentUnits() {
  const phone = useViewportMode() === 'mobile'
  const q = useQuery({ queryKey: ['network-units'], queryFn: () => get<{ units: NetworkUnit[] }>('/network/units') })
  // Đơn vị đã xoá không tính là tài khoản mới thêm
  const units = (q.data?.units ?? [])
    .filter((u) => u.status !== 'deleted')
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, LIMIT)

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl bg-raise shadow-card dark:border dark:border-rule dark:shadow-none">
      <div className="flex items-center justify-between gap-3 px-5 pb-1 pt-4 desktop:px-6">
        <h2>Tài khoản thêm gần đây</h2>
        <Link
          to="/network"
          className="motion-state -my-2.5 inline-flex min-h-11 shrink-0 items-center gap-0.5 text-[13px] font-semibold text-accent hover:underline desktop:my-0 desktop:min-h-8"
        >
          Xem tất cả
          <ChevronRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      {q.isError ? (
        <LoadError onRetry={() => q.refetch()} />
      ) : q.isLoading ? (
        <div className="space-y-3 px-5 pb-5 pt-3 desktop:px-6">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-11 w-full rounded-lg" />
          ))}
        </div>
      ) : units.length === 0 ? (
        <p className="px-5 pb-5 pt-2 text-sm text-muted desktop:px-6">Chưa có tài khoản nào</p>
      ) : (
        <ul className="pb-2">
          {units.map((u, i) => {
            const meta = networkTypeMeta(u.tenantType)
            const ownerPhone = u.owner?.phone ? phoneDisplay(u.owner.phone) : null
            const issue = networkDownlineIssues(u)[0]
            const pill = issue && (
              <span className={cn('truncate rounded-full px-2 py-0.5 text-[11px] font-medium', SEVERITY[issue.severity])}>{issue.label}</span>
            )
            return (
              <li key={u.id} className="border-t border-rule first:border-t-0">
                <Link
                  to={`/network?unit=${encodeURIComponent(u.id)}`}
                  className={cn(
                    'motion-state items-center gap-3 px-5 py-3 hover:bg-paperSoft/60 desktop:px-6',
                    phone ? 'flex' : 'grid grid-cols-[minmax(0,1fr)_11rem_8rem] gap-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_11rem_8rem]',
                  )}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-3">
                    <span className={cn(meta.tone, 'grid size-10 shrink-0 place-items-center rounded-full bg-toneBg text-xs font-bold text-toneFg')} aria-hidden>
                      {meta.short}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <IndexBadge n={i + 1} />
                        <span className="truncate text-sm font-semibold text-ink">{u.name}</span>
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {meta.label}
                        {u.parentName ? (
                          <> · dưới {u.parentName}</>
                        ) : (
                          ownerPhone && <span className={cn('tabular-nums', !phone && 'xl:hidden')}> · {ownerPhone}</span>
                        )}
                      </span>
                    </span>
                  </span>
                  {phone ? (
                    <span className="flex max-w-[45%] shrink-0 flex-col items-end gap-1">
                      <span className="text-xs tabular-nums text-muted">{recentWhen(u.createdAt)}</span>
                      {pill}
                    </span>
                  ) : (
                    <>
                      <span className="hidden truncate text-sm tabular-nums text-inkSoft xl:block">{ownerPhone ?? '—'}</span>
                      <span className="flex min-w-0 justify-end">{pill}</span>
                      <span className="text-right text-xs tabular-nums text-muted">{recentWhen(u.createdAt)}</span>
                    </>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
